import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { InputField } from '../components/ui/InputField';
import { AntiGravityBackground } from '../components/ui/AntiGravityBackground';
import loginBg from '../assets/login-bg.png';

const Register = () => {
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        password_confirm: ''
    });
    const { register } = useAuth();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.password !== formData.password_confirm) {
            alert("Passwords don't match");
            return;
        }
        setIsSubmitting(true);
        const success = await register(formData);
        if (success) {
            navigate('/login');
        }
        setIsSubmitting(false);
    };

    return (
        <div className="min-h-screen w-full flex overflow-hidden bg-white">
            {/* Left Side - Visual */}
            <div className="hidden lg:flex lg:w-1/2 relative bg-emba-dark text-white flex-col justify-between p-12 overflow-hidden">
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center brightness-[0.4] grayscale-[0.2]"
                    style={{ backgroundImage: `url(${loginBg})` }}
                />
                <AntiGravityBackground />
                <div className="relative z-10">
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-sm tracking-[0.4em] uppercase"
                    >
                        Seeker
                    </motion.h2>
                </div>

                <div className="relative z-10 mb-20">
                    <motion.h1
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="font-serif text-6xl leading-tight mb-6"
                    >
                        Premium Quality <br /> & Global Standards
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.7 }}
                        transition={{ duration: 1, delay: 0.6 }}
                        className="text-sm font-light tracking-wide max-w-md text-white/80"
                    >
                        Join our global marketplace to access exclusive catalogs,
                        track orders in real-time, and manage your wholesale
                        procurement with ease.
                    </motion.p>
                </div>

                <div className="relative z-10 text-xs tracking-widest text-white/40">
                    © 2026 SEEKER INC.
                </div>
            </div>

            {/* Right Side - Functional */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-12 relative overflow-y-auto">
                {/* Mobile Background Particle Fix */}
                <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-white via-sky-50 to-fuchsia-50 -z-10" />

                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    className="w-full max-w-md space-y-10 my-10 lg:my-0"
                >
                    <div className="text-center lg:text-left space-y-2">
                        <h2 className="font-serif text-3xl lg:text-4xl text-black">Create Account</h2>
                        <p className="text-gray-500 font-light">Begin your journey with Seeker.</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <InputField
                                label="First Name"
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                required
                            />
                            <InputField
                                label="Last Name"
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        <InputField
                            label="Email Address"
                            id="email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />

                        <InputField
                            label="Phone Number"
                            id="phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="024 XXX XXXX"
                            required
                        />

                        <InputField
                            label="Password"
                            id="password"
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required
                        />

                        <InputField
                            label="Confirm Password"
                            id="password_confirm"
                            type="password"
                            name="password_confirm"
                            value={formData.password_confirm}
                            onChange={handleChange}
                            required
                        />

                        <div className="pt-4 space-y-4">
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="w-full"
                            >
                                {isSubmitting ? 'Creating Account...' : 'Sign Up'}
                            </Button>

                            <div className="flex justify-center text-[10px] tracking-widest uppercase text-gray-400">
                                <span>
                                    Already a member?
                                    <Link to="/login" className="text-black ml-2 hover:underline underline-offset-4 decoration-1">
                                        Sign In
                                    </Link>
                                </span>
                            </div>
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default Register;
